import { useState, useEffect, useRef } from "react";

import type { ColumnEditorOptions } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { ConfirmDialog } from "primereact/confirmdialog";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";

import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { getCweTags } from "sj-ui-toolkit/src/api";
import { useDebounce } from "sj-ui-toolkit/src/hooks";
import {
  loadLessonsAsync,
  loadTagsCategoriesAsync,
  loadResourcesForLessonAsync,
} from "@/api/resources";
import type { Lesson, CustomResource, TagsData, LessonFilters } from "@/api/resources";
import { useResources } from "@/hooks/useResources";
import LessonFilterPanel from "@/components/resources/LessonFilterPanel";
import type { LessonFiltersState, MappedCweTag } from "@/components/resources/LessonFilterPanel";

// ---------------------------------------------------------------------------
// Inline column templates & editors (single-use, specific to this page)
// ---------------------------------------------------------------------------

const durationTemplate = (rowData: Lesson): string => `${rowData.duration || 0} min`;

const externalLinkTemplate = (rowData: CustomResource): JSX.Element | null => {
  if (!rowData.url) return null;
  const isUrl = rowData.url.startsWith("http://") || rowData.url.startsWith("https://");
  return isUrl ? (
    <a href={rowData.url} target="_blank" rel="noopener noreferrer" className="text-primary-darker">
      {rowData.url.length > 50 ? `${rowData.url.substring(0, 50)}...` : rowData.url}
    </a>
  ) : (
    <span>{rowData.url}</span>
  );
};

const textEditor = (options: ColumnEditorOptions): JSX.Element => (
  <InputText
    value={(options.value as string) || ""}
    onChange={e => options.editorCallback?.(e.target.value)}
    className="w-full"
  />
);

const textareaEditor = (options: ColumnEditorOptions): JSX.Element => (
  <InputTextarea
    value={(options.value as string) || ""}
    onChange={e => options.editorCallback?.(e.target.value)}
    rows={1}
    className="w-full"
  />
);

// ---------------------------------------------------------------------------
// Skeleton data
// ---------------------------------------------------------------------------

const SKELETON_LESSONS = Array.from({ length: DEFAULT_ROW_COUNT }, (_, i) => ({
  id: `skeleton-${i}`,
  name: null,
  description: null,
  duration: null,
  isSkeleton: true,
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CustomResources = () => {
  useBreadcrumb({
    label: "Custom Resources",
    route: AdminAppRoutes.routes.resources.route,
    display: "none",
    resetTrail: true,
  });

  const moduleDetailsRef = useRef<HTMLDivElement>(null);

  // Lessons state
  const [availableLessons, setAvailableLessons] = useState<Lesson[]>([]);
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  // Resources state
  const [customResources, setCustomResources] = useState<CustomResource[]>([]);
  const [loadingResources, setLoadingResources] = useState(false);

  // Filter state
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filters, setFilters] = useState<LessonFiltersState>({
    focus: [],
    difficulty: [],
    release: [],
    contentType: [],
    languages: [],
    technologies: [],
    cwe: [],
  });
  const [tagsData, setTagsData] = useState<TagsData>({ tagsCategories: [], cweTags: [] });
  const [cweTags, setCweTags] = useState<MappedCweTag[]>([]);

  const {
    showCreateDialog,
    setShowCreateDialog,
    newResource,
    setNewResource,
    selectedResources,
    setSelectedResources,
    handleCreateResource,
    isCreating,
    handleBulkDeleteResources,
    onRowEditComplete,
  } = useResources({ selectedLesson, setCustomResources });

  // Load tags
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const tags = await loadTagsCategoriesAsync();
        setTagsData(tags);
      } catch (error) {
        console.error("Failed to load tags:", error);
      }
    };
    fetchTags();
  }, []);

  // Enrich CWE tags from static CDN data once tagsData is available
  useEffect(() => {
    const loadCweTags = async () => {
      const cweIdsInUse =
        tagsData.cweTags
          ?.map(cwe => parseInt(cwe.cweTag?.name?.replace("CWE-", "") ?? "", 10))
          .filter(n => !isNaN(n)) ?? [];
      if (cweIdsInUse.length === 0) return;

      const cweData = (await getCweTags()) as Record<number, { Name: string; Description: string }>;
      const mapped: MappedCweTag[] = cweIdsInUse
        .map(cweNumber => {
          const info = cweData[cweNumber];
          return {
            value: cweNumber,
            name: info ? `CWE-${cweNumber}: ${info.Name}` : `CWE-${cweNumber}`,
            description: info?.Description ?? null,
          };
        })
        .sort((a, b) => a.value - b.value);
      setCweTags(mapped);
    };
    loadCweTags();
  }, [tagsData]);

  // Reload lessons when filters change
  const debouncedFilters = useDebounce(filters, 350);
  useEffect(() => {
    let cancelled = false;
    setLoadingLessons(true);
    const fetchLessons = async () => {
      const hasActiveFilters = Object.values(debouncedFilters).some(
        (arr: unknown) => Array.isArray(arr) && arr.length > 0
      );
      try {
        const apiFilters: LessonFilters = hasActiveFilters
          ? {
              tags: [
                ...(debouncedFilters.languages ?? []),
                ...(debouncedFilters.technologies ?? []),
                ...(debouncedFilters.focus ?? []),
              ],
              cwe_tags: (debouncedFilters.cwe ?? []).map((id: number) => `CWE-${id}`),
              difficulty: debouncedFilters.difficulty,
              lesson_type: debouncedFilters.contentType,
              release: debouncedFilters.release,
            }
          : {};
        const lessons = await loadLessonsAsync(apiFilters);
        if (!cancelled) setAvailableLessons(lessons);
      } catch (error) {
        console.error("Failed to load filtered lessons:", error);
      } finally {
        if (!cancelled) setLoadingLessons(false);
      }
    };
    fetchLessons();
    return () => {
      cancelled = true;
    };
  }, [debouncedFilters]);

  // Load resources when lesson is selected
  useEffect(() => {
    if (!selectedLesson) {
      setCustomResources([]);
      return;
    }
    let cancelled = false;
    const fetchResources = async () => {
      try {
        setLoadingResources(true);
        const response = await loadResourcesForLessonAsync(selectedLesson.id);
        if (!cancelled) setCustomResources(response?.customResources ?? []);
      } catch (error) {
        console.error("Failed to load resources:", error);
      } finally {
        if (!cancelled) setLoadingResources(false);
      }
    };
    fetchResources();
    return () => {
      cancelled = true;
    };
  }, [selectedLesson]);

  const handleLessonSelect = (lesson: Lesson | null) => {
    setSelectedLesson(lesson);
    if (lesson && moduleDetailsRef.current) {
      setTimeout(() => {
        moduleDetailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  };

  const handleFilterChange = (category: string, values: string[] | number[]) => {
    setFilters(prev => ({ ...prev, [category]: values }));
  };

  const handleClearFilters = () => {
    setFilters({
      focus: [],
      difficulty: [],
      release: [],
      contentType: [],
      languages: [],
      technologies: [],
      cwe: [],
    });
  };

  return (
    <>
      <div className="mb-4">
        <h1>Custom Resources</h1>
      </div>

      <div className={`p-4 min-h-[60vh] ${themeClasses.bgElevation1}`}>
        <div className="space-y-6">
          {/* Available Lessons */}
          <div className="space-y-4">
            <div className="p-4">
              <h2 className="atlas-h4 font-semibold mb-4">Available Lessons</h2>

              <LessonFilterPanel
                isOpen={isFilterPanelOpen}
                onToggle={() => setIsFilterPanelOpen(prev => !prev)}
                tagsData={tagsData}
                cweTags={cweTags}
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />

              <DataTable
                value={loadingLessons ? SKELETON_LESSONS : availableLessons}
                paginator
                aria-label="Available lessons"
                rows={DEFAULT_ROW_COUNT}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                selectionMode="single"
                selection={selectedLesson}
                onSelectionChange={e => handleLessonSelect(e.value as Lesson | null)}
                globalFilter={globalFilterValue}
                globalFilterFields={["name", "description"]}
                emptyMessage="No lessons available"
                header={
                  <div className="flex justify-start items-center">
                    <div className="max-w-md w-full">
                      <IconField iconPosition="right">
                        <InputIcon className="pi pi-search" />
                        <InputText
                          value={globalFilterValue}
                          onChange={e => setGlobalFilterValue(e.target.value)}
                          placeholder="Search lessons..."
                          aria-label="Search lessons"
                          className="w-full"
                        />
                      </IconField>
                    </div>
                  </div>
                }
                size="large"
                className="mt-4"
                data-testid="available-lessons-table"
                tableClassName="dt-responsive-text"
              >
                <Column selectionMode="single" headerStyle={{ width: "3rem" }} />
                <Column
                  field="name"
                  header="Lesson Name"
                  body={loadingLessons ? <Skeleton /> : undefined}
                  sortable
                  className="font-medium"
                />
                <Column
                  field="description"
                  header="Description"
                  body={loadingLessons ? <Skeleton /> : undefined}
                />
                <Column
                  field="duration"
                  header="Duration"
                  body={loadingLessons ? <Skeleton /> : durationTemplate}
                  sortable
                  className="text-center"
                />
              </DataTable>
            </div>
          </div>

          {/* Lesson Details */}
          {selectedLesson && (
            <div className="px-4" ref={moduleDetailsRef}>
              <h3 className="text-lg font-semibold mb-4">Lesson Details</h3>
              <div className="space-y-2">
                <p>
                  <strong>Name:</strong> {selectedLesson.name}
                </p>
                <p>
                  <strong>Description:</strong> {selectedLesson.description}
                </p>
                <p>
                  <strong>Duration:</strong> {selectedLesson.duration} min
                </p>
              </div>
            </div>
          )}

          {/* Custom Resources */}
          <DataTable
            value={customResources}
            editMode="row"
            aria-label="Custom resources"
            dataKey="id"
            onRowEditComplete={e =>
              onRowEditComplete({ newData: e.newData as CustomResource, index: e.index })
            }
            loading={loadingResources}
            paginator
            rows={5}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            selectionMode="multiple"
            selection={selectedResources}
            onSelectionChange={e => setSelectedResources(e.value as CustomResource[])}
            emptyMessage={
              selectedLesson ? "No custom resources yet" : "Select a lesson to view resources"
            }
            header={
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Custom Resources</h3>
                  {selectedResources.length > 0 && (
                    <span className="body-sm text-secondary">
                      ({selectedResources.length} selected)
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {selectedResources.length > 0 && (
                    <Button
                      label={`Delete (${selectedResources.length})`}
                      icon="pi pi-trash"
                      severity="danger"
                      onClick={handleBulkDeleteResources}
                    />
                  )}
                  <Button
                    label="Add Resource"
                    icon="pi pi-plus"
                    onClick={() => setShowCreateDialog(true)}
                    disabled={!selectedLesson}
                  />
                </div>
              </div>
            }
            size="large"
            data-testid="custom-resources-table"
            tableClassName="dt-responsive-text"
          >
            <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
            <Column
              field="name"
              header="Name"
              editor={options => textEditor(options)}
              sortable
              className="font-medium"
            />
            <Column
              field="description"
              header="Description"
              editor={options => textareaEditor(options)}
            />
            <Column
              field="url"
              header="URL/Content"
              editor={options => textareaEditor(options)}
              body={externalLinkTemplate}
            />
            <Column
              field="createdAt"
              header="Created"
              body={(rowData: CustomResource) => new Date(rowData.createdAt).toLocaleDateString()}
              sortable
              className="text-center"
            />
            <Column
              rowEditor
              headerStyle={{ width: "10%", minWidth: "8rem" }}
              bodyStyle={{ textAlign: "center" }}
            />
          </DataTable>
        </div>
      </div>

      {/* Create Resource Dialog */}
      <Dialog
        header="Create Custom Resource"
        visible={showCreateDialog}
        onHide={() => {
          setShowCreateDialog(false);
          setNewResource({ name: "", description: "", url: "" });
        }}
        className="w-full max-w-2xl"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="create-resource-name" className="block body-sm mb-2">
              Name
            </label>
            <InputText
              id="create-resource-name"
              value={newResource.name}
              onChange={e => setNewResource(prev => ({ ...prev, name: e.target.value }))}
              className="w-full"
              placeholder="Enter resource name"
            />
          </div>
          <div>
            <label htmlFor="create-resource-description" className="block body-sm mb-2">
              Description
            </label>
            <InputTextarea
              id="create-resource-description"
              value={newResource.description}
              onChange={e => setNewResource(prev => ({ ...prev, description: e.target.value }))}
              rows={1}
              className="w-full"
              placeholder="Enter resource description"
            />
          </div>
          <div>
            <label htmlFor="create-resource-url" className="block body-sm mb-2">
              URL
            </label>
            <InputTextarea
              id="create-resource-url"
              value={newResource.url}
              onChange={e => setNewResource(prev => ({ ...prev, url: e.target.value }))}
              rows={1}
              className="w-full"
              placeholder="Enter resource URL"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button label="Cancel" severity="secondary" onClick={() => setShowCreateDialog(false)} />
          <Button
            label="Create"
            onClick={handleCreateResource}
            loading={isCreating}
            disabled={!newResource.name.trim()}
          />
        </div>
      </Dialog>

      <ConfirmDialog maskStyle={{ backdropFilter: "blur(5px)" }} />
    </>
  );
};

export default CustomResources;
